import { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const useAdminWhitelist = ({ Admin }) => {
  const { baseUrl, basicAuth } = useContext(GlobalContext);

  const hasFetchedRef = useRef(false);

  const config = {
    headers: {
      Authorization: basicAuth,
      "Content-Type": "application/json",
    },
  };

  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [locationReady, setLocationReady] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationReady(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
        setLocationReady(true);
      },
      (err) => {
        toast.error(err.message);
        setLocationReady(true);
      }
    );
  }, []);

  const fetchClients = async () => {
    if (!Admin?.pkid || !baseUrl || !basicAuth) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${baseUrl}/SuperAdmin/getWhiteListedAudience`,
        {
          userid: Admin.pkid,
          latitude,
          longitude,
        },
        config
      );

      if (response.data.responseCode === "00") {
        setClients(response.data.response || []);
      } else {
        toast.error(response.data.responseMessage);
      }
    } catch (error) {
      toast.error("Error fetching clients");
      console.error("Error fetching whitelist clients:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!locationReady) return;
    if (!Admin?.pkid) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchClients();
  }, [locationReady, Admin?.pkid]);

  const addClient = async () => {
    if (!name.trim()) {
      toast.error("Please enter name");
      return;
    }

    if (!mobile.trim()) {
      toast.error("Please enter mobile number");
      return;
    }

    try {
      setActionLoading(true);

      const response = await axios.post(
        `${baseUrl}/SuperAdmin/addWhiteListedAudience`,
        {
          userid: Admin.pkid,
          mobile,
          name,
        },
        config
      );

      if (response.data.responseCode === "00") {
        toast.success(response.data.responseMessage);
        setShowModal(false);
        setName("");
        setMobile("");
        fetchClients();
      } else {
        toast.error(response.data.responseMessage);
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error adding whitelist client:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteClient = async (id) => {
    if (!id) return;

    try {
      setActionLoading(true);

      const response = await axios.post(
        `${baseUrl}/SuperAdmin/removeWhiteListedAudience`,
        {
          userid: Admin.pkid,
          audienceId: id,
        },
        config
      );

      if (response.data.responseCode === "00") {
        toast.success(response.data.responseMessage);
        fetchClients();
      } else {
        toast.error(response.data.responseMessage);
      }
    } catch (error) {
      toast.error("Error deleting client");
      console.error("Error deleting whitelist client:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    const search = searchTerm.toLowerCase();

    return clients.filter((client) =>
      Object.values(client).some((value) =>
        value?.toString().toLowerCase().includes(search)
      )
    );
  }, [clients, searchTerm]);

  return {
    clients,
    filteredClients,

    searchTerm,
    setSearchTerm,

    loading,
    actionLoading,

    showModal,
    setShowModal,

    name,
    setName,
    mobile,
    setMobile,

    latitude,
    longitude,

    fetchClients,
    addClient,
    deleteClient,
  };
};

export default useAdminWhitelist;